import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Save, Loader2 } from 'lucide-react';
import { api } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { useCepLookup } from '../../hooks';
import { PageHeader, InputMask } from '../../components/ui';
import { Panel, buttonClass } from '../../design-system';

const clienteSchema = z.object({
  nome: z.string().min(1, 'Nome é obrigatório'),
  tipo: z.literal('CLIENTE').default('CLIENTE'),
  tipoPessoa: z.enum(['PF', 'PJ']),
  cpfCnpj: z.string().optional(),
  rgIe: z.string().optional(),
  email: z.string().email('Email inválido').optional().or(z.literal('')),
  telefone: z.string().optional(),
  celular: z.string().optional(),
  enderecoCep: z.string().optional(),
  enderecoLogradouro: z.string().optional(),
  enderecoNumero: z.string().optional(),
  enderecoComplemento: z.string().optional(),
  enderecoBairro: z.string().optional(),
  enderecoCidade: z.string().optional(),
  enderecoUf: z.string().optional(),
  dataNascimento: z.string().optional(),
  observacoes: z.string().optional(),
});

type ClienteFormData = z.infer<typeof clienteSchema>;

export function ClienteForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const isEditing = !!id;

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ClienteFormData>({
    resolver: zodResolver(clienteSchema),
    defaultValues: {
      tipo: 'CLIENTE',
      tipoPessoa: 'PF',
    },
  });

  const tipoPessoa = watch('tipoPessoa');

  // Hook para busca de CEP
  const { fetchCep } = useCepLookup({
    onSuccess: (data) => {
      setValue('enderecoLogradouro', data.logradouro);
      setValue('enderecoBairro', data.bairro);
      setValue('enderecoCidade', data.localidade);
      setValue('enderecoUf', data.uf);
    },
  });

  // Carregar cliente para edição
  useEffect(() => {
    if (!id) return;
    const controller = new AbortController();
    setIsLoading(true);
    api.get<Record<string, unknown>>(`/pessoas/${id}`, { signal: controller.signal })
      .then((response) => {
        const cliente = response.data;
        Object.keys(cliente).forEach((key) => {
          // Converter para camelCase do schema
          const schemaKey = key.charAt(0).toLowerCase() + key.slice(1);
          if (schemaKey in clienteSchema.shape) {
            setValue(schemaKey as keyof ClienteFormData, (cliente[key] ?? '') as ClienteFormData[keyof ClienteFormData]);
          }
        });
      })
      .catch((error) => {
        // Ignorar erros de cancelamento (AbortController)
        if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return;
        showError('Erro ao carregar cliente');
      })
      .finally(() => setIsLoading(false));
    return () => controller.abort();
  }, [id, setValue, showError]);

  const onSubmit = async (data: ClienteFormData) => {
    setIsSaving(true);
    try {
      // Garantir que tipo é sempre CLIENTE
      const payload = { ...data, tipo: 'CLIENTE' };

      if (isEditing) {
        await api.put(`/pessoas/${id}`, payload);
      } else {
        await api.post('/pessoas', payload);
      }

      showSuccess(isEditing ? 'Cliente atualizado com sucesso' : 'Cliente cadastrado com sucesso');
      navigate('/clientes');
    } catch {
      showError('Erro ao salvar cliente');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={isEditing ? 'Editar Cliente' : 'Novo Cliente'}
        subtitle={isEditing ? 'Altere os dados do cliente' : 'Cadastre um novo cliente'}
        showBack
        backTo="/clientes"
      />

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Dados Básicos */}
        <Panel title="Dados Básicos">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="md:col-span-2">
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Nome *
              </label>
              <input
                type="text"
                {...register('nome')}
                className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-700 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
              {errors.nome && (
                <p className="mt-1 text-sm text-rose-600">{errors.nome.message}</p>
              )}
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Tipo de Pessoa
              </label>
              <select
                {...register('tipoPessoa')}
                className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-700 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              >
                <option value="PF">Pessoa Física</option>
                <option value="PJ">Pessoa Jurídica</option>
              </select>
            </div>

            <Controller
              name="cpfCnpj"
              control={control}
              render={({ field }) => (
                <InputMask
                  label={tipoPessoa === 'PF' ? 'CPF' : 'CNPJ'}
                  mask={tipoPessoa === 'PF' ? 'cpf' : 'cnpj'}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder={tipoPessoa === 'PF' ? '000.000.000-00' : '00.000.000/0000-00'}
                />
              )}
            />

            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">
                {tipoPessoa === 'PF' ? 'RG' : 'Inscrição Estadual'}
              </label>
              <input
                type="text"
                {...register('rgIe')}
                className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-700 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </div>

            {tipoPessoa === 'PF' && (
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">
                  Data de Nascimento
                </label>
                <input
                  type="date"
                  {...register('dataNascimento')}
                  className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-700 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
              </div>
            )}
          </div>
        </Panel>

        {/* Contato */}
        <Panel title="Contato">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="md:col-span-2">
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Email
              </label>
              <input
                type="email"
                {...register('email')}
                placeholder="email@exemplo.com"
                className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-700 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-rose-600">{errors.email.message}</p>
              )}
            </div>

            <Controller
              name="telefone"
              control={control}
              render={({ field }) => (
                <InputMask
                  label="Telefone"
                  mask="phone"
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="(00) 0000-0000"
                />
              )}
            />

            <Controller
              name="celular"
              control={control}
              render={({ field }) => (
                <InputMask
                  label="Celular"
                  mask="cellphone"
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="(00) 00000-0000"
                />
              )}
            />
          </div>
        </Panel>

        {/* Endereço */}
        <Panel title="Endereço">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <Controller
              name="enderecoCep"
              control={control}
              render={({ field }) => (
                <InputMask
                  label="CEP"
                  mask="cep"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={() => field.value && fetchCep(field.value)}
                  placeholder="00000-000"
                />
              )}
            />

            <div className="md:col-span-2">
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Logradouro
              </label>
              <input
                type="text"
                {...register('enderecoLogradouro')}
                className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-700 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Número
              </label>
              <input
                type="text"
                {...register('enderecoNumero')}
                className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-700 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </div>

            <div className="md:col-span-2">
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Complemento
              </label>
              <input
                type="text"
                {...register('enderecoComplemento')}
                className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-700 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Bairro
              </label>
              <input
                type="text"
                {...register('enderecoBairro')}
                className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-700 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Cidade
              </label>
              <input
                type="text"
                {...register('enderecoCidade')}
                className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-700 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">
                UF
              </label>
              <select
                {...register('enderecoUf')}
                className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-700 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              >
                <option value="">Selecione...</option>
                {['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'].map((uf) => (
                  <option key={uf} value={uf}>{uf}</option>
                ))}
              </select>
            </div>
          </div>
        </Panel>

        {/* Observações */}
        <Panel title="Observações">
          <textarea
            {...register('observacoes')}
            rows={4}
            placeholder="Observações sobre o cliente..."
            className="w-full resize-none rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-700 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </Panel>

        {/* Actions */}
        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate('/clientes')}
            className={buttonClass('secondary')}
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className={buttonClass('primary')}
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {isSaving ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </form>
    </div>
  );
}
